import React, { useState, ReactNode, PropsWithChildren } from 'react';
import classNames from 'classnames';
import { SvgNames } from '../../../foundations/Icons/svg';
import Icon from '../../../foundations/Icons';
import { SkeletonLoader } from '../../Skeleton';
import { ColorCodes } from '../../../foundations/color';
import { WarningTooltip } from '../../Base/Tooltip';

type CardVariant = 'default' | 'grid';

export type GenericCardProps = PropsWithChildren<{
  title: string | ReactNode;
  icon?: SvgNames;
  iconNode?: ReactNode;
  iconFill?: ColorCodes;
  /** The card's right column */
  rightView?: ReactNode;
  /** Turns the card into a collapsible card */
  collapsible?: boolean;
  /** Card content is open and shown when the generic card is collapsible */
  open?: boolean;
  disabled?: boolean;
  /** The card's class names */
  className?: string;
  /** disable padding inside the card content */
  padded?: boolean;
  lozenge?: string;
  variant?: CardVariant;
  tooltip?: string;
}>;

export const GenericCard = (props: GenericCardProps) => {
  const {
    children,
    collapsible,
    className,
    disabled,
    rightView,
    open,
    padded = true,
    title,
    icon,
    iconNode,
    variant = 'default',
    tooltip,
  } = props;
  const [isOpen, setOpen] = useState(open || false);

  return (
    <div
      className={classNames(
        'mb-s flex flex-col divide-y divide-background-subtle rounded border border-foreground-subtle bg-regular',
        { 'rounded-b': isOpen },
        { 'opacity-40 pointer-events-none': disabled },
        { 'h-full': variant === 'grid' },
        className
      )}
    >
      <div
        className={classNames(
          'flex h-auto min-h-[60px] flex-row gap-xxxs px-l py-xxs bg-subtle',
          { 'flex-1': variant === 'default' },
          { 'flex-0': variant === 'grid' }
        )}
        {...(collapsible && {
          onClick: () => setOpen(!isOpen),
          onKeyDown: () => setOpen(!isOpen),
          role: 'button',
        })}
      >
        <div className="flex flex-1 items-center gap-xxs">
          {title ? (
            <>
              {icon && <Icon name={icon} />}
              {!!iconNode && iconNode}
              <p className="font-body text-body font-bold">{title}</p>
              {tooltip && <WarningTooltip content={tooltip} />}
            </>
          ) : (
            <SkeletonLoader widthClass="w-full" />
          )}
          <div className="font-body text-body font-bold text-strong" />
        </div>

        {collapsible && (
          <div className="flex items-center">
            <div className={classNames('transition', { 'rotate-180': isOpen })}>
              <Icon name="sChevronDown" />
            </div>
          </div>
        )}
        {!!rightView && <div className="flex items-center">{rightView}</div>}
      </div>

      {collapsible ? (
        isOpen && (
          <div
            className={classNames('bg-regular', {
              'p-l': padded,
              relative: true,
            })}
          >
            {children}
          </div>
        )
      ) : (
        <div
          className={classNames('bg-regular', {
            'p-l': padded,
            relative: true,
          })}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export const Hint = ({ children }: PropsWithChildren<{}>) => (
  <div className="font-body text-body-small text-subtle">{children}</div>
);

export default GenericCard;
